import React from 'react'
import Tooltip from '../Tooltip'
import { Link } from 'react-router-dom'
import { generateCreateUrlFromCategory } from '#src/services/path'
import classNames from 'classnames'
import GolferImage from './golfer.jpg'
import UnderlinedText from '#src/components/UnderlinedText'
import { GridItem, Button } from '@deal/components'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import camelCase from 'lodash/camelCase'
import upperFirst from 'lodash/upperFirst'
import lowerCase from 'lodash/lowerCase'
import styles from './styles.css'
import {
  SellableSpecificationFragment,
  SellableDetailsVariantFragment
} from '#src/generated/graphql'
import BackgroundImage from '#src/components/BackgroundImage'

interface SpecificationTableProps {
  className?: string
  specifications: SellableSpecificationFragment[]
  loftType?: SellableDetailsVariantFragment
  shaft?: SellableDetailsVariantFragment
}

const SpecificationTable: React.SFC<SpecificationTableProps> = ({
  specifications,
  className,
  loftType,
  shaft
}) => {
  return (
    <div className={classNames(styles.specifications, className)}>
      <div className={styles.specificationsHeader}>Specifications</div>

      <div className={styles.highlightedSpecifications}>
        {loftType && (
          <div className={styles.attribute}>
            <UnderlinedText thickness={2} underlineColor="#e6e6e6" className={styles.underline}>
              <div className={styles.specificationHeader}>Loft</div>
            </UnderlinedText>

            <div
              className={styles.specificationValue}
              dangerouslySetInnerHTML={{
                __html: loftType.displayValueHtml
              }}
            />
          </div>
        )}
        {shaft && (
          <div className={styles.attribute}>
            <UnderlinedText thickness={2} underlineColor="#e6e6e6" className={styles.underline}>
              <div className={styles.specificationHeader}>Shaft material</div>
            </UnderlinedText>

            <div
              className={styles.specificationValue}
              dangerouslySetInnerHTML={{
                __html: shaft.displayValueHtml
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.otherInformationHeader}>Other information</div>

      <div className={styles.otherSpecifications}>
        {specifications
          .filter(spec => spec.name !== 'details')
          .map(spec => {
            return (
              <div className={styles.otherSpec} key={spec.name}>
                <div className={styles.otherSpecLabel}>{upperFirst(lowerCase(spec.name))}</div>
                <div
                  className={styles.otherSpecValue}
                  dangerouslySetInnerHTML={{
                    __html: spec.valueHtml
                  }}
                />
              </div>
            )
          })}
      </div>
    </div>
  )
}

interface GolfSpecificationsProps {
  isProductDetailsNotesVisible?: boolean
  hasRequestForDepartment: boolean
  variants: SellableDetailsVariantFragment[]
  specifications: SellableSpecificationFragment[]
}

const flexDescriptions: { [key: string]: string } = {
  regular:
    'Optimal for high handicap golfers able to drive a ball 210 to 250 yards off the tee. Regular flex shafts are the most popular choice for golfers with a swing speed between 85 and 95 mph.',
  senior:
    'Ideal for golfers who, with aging, have noticed a decline in their swing speed and drive distance. Senior flex shafts are generally suited for golfers with a swing between 75 and 85 mph with a carry distance of 180 to 210 yards.',
  stiff:
    'Best suited for mid-to-low handicap golfers able to drive a ball 250+ yards. They are typically used by golfers with a 95 to 110 mph swing.',
  extraStiff:
    'Golfers able to drive a ball 275+ yards with a swing speed of 110+ mph may prefer an extra stiff shaft. These can be challenging to find, as they are relatively unpopular amongst non-professionals.',
  ladies:
    'A good fit for golfers with a sub 180 yard carry distance and a swing of less than 75 mph. Women with a longer drive and faster swing may want to consider senior or regular flex clubs.'
}

const GolfSpecifications: React.SFC<GolfSpecificationsProps> = ({
  isProductDetailsNotesVisible,
  specifications,
  variants,
  hasRequestForDepartment
}) => {
  // Hide the entire module if there are no specifications
  if (specifications.length === 0) {
    return null
  }

  const flexType = variants.find(variant => variant.name === 'flexType')
  const loftType = variants.find(variant => variant.name === 'Loft')
  const shaft = variants.find(variant => variant.name === 'Shaft')

  if (flexType && flexType.displayName && flexType.value) {
    const formattedDisplayValue = camelCase(flexType.value)

    return (
      <div className={styles.fullWrapper}>
        <div className={styles.darkWrapper}>
          <SidebarAwareGrid>
            <GridItem col={2}>
              <div className={styles.innerWrapper}>
                <BackgroundImage
                  src={GolferImage}
                  containerClassName={styles.container}
                  contentClassName={styles.imageWrapper}
                  imageClassName={styles.image}
                  size="100vw"
                >
                  <div className={styles.attributeHeader}>
                    Flex Type{' '}
                    <span className={styles.tooltip}>
                      <Tooltip reference={flexType.name} fill="white" title={'Flex Type'} />
                    </span>
                  </div>
                  <div
                    className={styles.attributeLabel}
                    dangerouslySetInnerHTML={{
                      __html: flexType.displayValueHtml
                    }}
                  />

                  <div className={styles.attributeDescription}>
                    {flexDescriptions[formattedDisplayValue]}
                  </div>
                  {!hasRequestForDepartment && (
                    <div className={styles.actions}>
                      <Link
                        to={generateCreateUrlFromCategory({
                          maybeCategorySlug: 'golf',
                          pathIntent: 'register',
                          sourceKey: 'golf-specifications-flex-module'
                        })}
                      >
                        <Button className={styles.buttonClear} size="small">
                          <span className={styles.mobile}>Find the pefect flex for you →</span>
                          <span className={styles.desktop}>
                            Take a short quiz to find the perfect flex for you →
                          </span>
                        </Button>
                      </Link>
                    </div>
                  )}
                </BackgroundImage>
                <div className={classNames(styles.lightWrapper, styles.desktop)}>
                  <SpecificationTable
                    specifications={specifications}
                    loftType={loftType}
                    shaft={shaft}
                  />
                </div>
              </div>
            </GridItem>
          </SidebarAwareGrid>
        </div>
        <div className={classNames(styles.lightWrapper, styles.mobile)}>
          <SpecificationTable specifications={specifications} loftType={loftType} shaft={shaft} />
        </div>
      </div>
    )
  } else if (shaft || loftType) {
    return (
      <div
        className={classNames(styles.fullWrapper, {
          [styles.noSpacingWrapper]: isProductDetailsNotesVisible
        })}
      >
        <div className={!isProductDetailsNotesVisible ? styles.lightWrapper : styles.whiteWrapper}>
          <SidebarAwareGrid>
            <GridItem col={2}>
              <SpecificationTable
                className={styles.standaloneSpecifications}
                specifications={specifications}
                loftType={loftType}
                shaft={shaft}
              />
            </GridItem>
          </SidebarAwareGrid>
        </div>
      </div>
    )
  }

  return null
}

export default GolfSpecifications
